import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Actives, Filter, FilterOp } from '@homeleite/arch';
import { User, UsersRepository } from '@homeleite/domain';

import { BehaviorSubject, combineLatest, concat, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, startWith, switchMap, timeout } from 'rxjs/operators';

import { ProducersApiRepository } from '../../../../../../../libs/domain/src/lib/producers/producers-api.repository';
import { ProducerPicker } from './controller-details-producers/controller-details-producers.component';

@Component({
    selector: 'app-controller-details',
    templateUrl: './controller-details.component.html',
    styleUrls: ['./controller-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ControllerDetailsComponent implements OnInit {
    @ViewChild('detailsForm') detailsForm: ControllerDetailsComponent;
    @Input() entity: any;
    @Output() formSubmit = new EventEmitter<any>();
    @Output() statusChange = new EventEmitter<boolean>();

    associatedUsers$: Observable<User[]>;
    form: FormGroup;
    readonly producerPickerMinLength = 2;
    readonly producerPickerLimit = 20;
    producerSearchCtrl = new FormControl('');
    filteredProducers$: Observable<ProducerPicker>;
    selectedProducers: any[] = [];
    readonly sections = [
        { label: 'Controles', icon: 'fact_check' },
        { label: 'Dados gerais', icon: 'badge' },
        { label: 'Produtores', icon: 'agriculture' },
        { label: 'Usuários', icon: 'group' },
        { label: 'Logs', icon: 'history' }
    ];
    selectedTabIndex = 0;
    private selection$ = new BehaviorSubject<any[]>([]);
    private saveTrigger$ = new Subject<void>();
    private saveSubscription: Subscription;

    constructor(
        private fb: FormBuilder,
        private producerRepository: ProducersApiRepository,
        private usersRepository: UsersRepository
    ) {}

    ngOnInit() {
        this.form = this.fb.group({
            name: ['', Validators.required],
            code: [''],
            email: ['', Validators.email]
        });

        if (this.entity) {
            this.loadAssociatedUsers();
            this.form.patchValue(this.entity);
            this.selectedProducers = [...(this.entity.producers || [])];
        }
        this.syncSelection();

        const results$ = this.producerSearchCtrl.valueChanges.pipe(
            startWith(''),
            map((value) => (typeof value === 'string' ? value.trim() : '')),
            debounceTime(300),
            distinctUntilChanged(),
            switchMap((term) =>
                term.length < this.producerPickerMinLength
                    ? of<ProducerPicker>({ status: 'idle' })
                    : concat(
                          of<ProducerPicker>({ status: 'loading' }),
                          this.producerRepository.list(term).pipe(
                              timeout(10000),
                              map(
                                  (producers) =>
                                      ({ status: 'ready', items: producers, total: producers.length }) as ProducerPicker
                              ),
                              catchError(() => of<ProducerPicker>({ status: 'error' }))
                          )
                      )
            )
        );

        this.filteredProducers$ = combineLatest([results$.pipe(startWith<ProducerPicker>({ status: 'idle' })), this.selection$]).pipe(
            map(([picker, selected]) => {
                if (picker.status !== 'ready') return picker;
                const linked = new Set(selected.map((p) => p.id));
                const available = picker.items.filter((producer) => !linked.has(producer.id));
                return {
                    status: 'ready',
                    items: available.slice(0, this.producerPickerLimit),
                    total: available.length
                } as ProducerPicker;
            })
        );

        this.saveSubscription = this.saveTrigger$.subscribe(() => this.autoSaveProducers());
    }

    loadAssociatedUsers() {
        const controllerFilter = new Filter('controller.$id', FilterOp.EQ, this.entity.id);

        this.associatedUsers$ = this.usersRepository.list('', [controllerFilter], [], Actives.TRUE);
    }

    autoSaveProducers(): void {
        const result = {
            ...this.entity,
            ...this.form.value,
            producers: this.selectedProducers,
            active: this.entity ? this.entity.active : true
        };
        this.formSubmit.emit(result);
    }

    onProducerSelected(producerToAdd: any): void {
        if (producerToAdd && !this.selectedProducers.some((p) => p.id === producerToAdd.id)) {
            this.selectedProducers.push(producerToAdd);
            this.syncSelection();
            this.saveTrigger$.next();
        }
    }

    onProducerRemoved(producer: any): void {
        const index = this.selectedProducers.findIndex((p) => p.id === producer.id);

        if (index >= 0) {
            this.selectedProducers.splice(index, 1);
            this.syncSelection();
            this.saveTrigger$.next();
        }
    }

    private syncSelection(): void {
        this.selection$.next([...this.selectedProducers]);
    }

    onSubmit() {
        if (this.form.invalid) {
            return;
        }

        const result = {
            ...this.entity,
            ...this.form.value,
            producers: this.selectedProducers,
            active: this.entity ? this.entity.active : true
        };
        this.formSubmit.emit(result);
    }

    changeStatus(newStatus: boolean): void {
        if (this.entity) {
            this.entity.active = newStatus;
        }
    }
}
